import { AUTHORING_CHAT_TOOL_FILTER, ChatPanel } from "@qw/chat";
import { MCPPlatform } from "@qw/qw-common";
import { useMemo, useRef } from "react";

import { useChatResourceLinkHandlers } from "../../chat/use-chat-resource-link-handlers";

const AUTHORING_CHAT_MODE = "authoring";
const PLAYBOOK_AUTHORING_ENTITY_TYPE = "playbook";

interface PlaybookEditorChatPanelProps {
  currentWorkflowHtml: string;
  onInsert: (html: string) => void;
  playbookId: string;
}

export const PlaybookEditorChatPanel = ({
  currentWorkflowHtml,
  onInsert,
  playbookId,
}: PlaybookEditorChatPanelProps) => {
  const resourceLinkHandlers = useChatResourceLinkHandlers();
  const defaultPromptInputRef = useRef<{
    input: {
      draftContent: string;
      entityId: string;
      entityType: string;
      mode: string;
    };
    playbookId: string;
  } | null>(null);
  const authoringMetadata = useMemo(
    () => [
      { key: "mode", value: AUTHORING_CHAT_MODE },
      { key: "entityType", value: PLAYBOOK_AUTHORING_ENTITY_TYPE },
      { key: "entityId", value: playbookId },
    ],
    [playbookId]
  );
  const chatSessionsFilter = useMemo(() => ({ metadata: authoringMetadata }), [authoringMetadata]);
  const promptInput = useMemo(
    () => ({
      mode: AUTHORING_CHAT_MODE,
      entityType: PLAYBOOK_AUTHORING_ENTITY_TYPE,
      entityId: playbookId,
      draftContent: currentWorkflowHtml,
    }),
    [currentWorkflowHtml, playbookId]
  );

  if (defaultPromptInputRef.current?.playbookId !== playbookId) {
    defaultPromptInputRef.current = { playbookId, input: promptInput };
  }
  const defaultPromptInput = defaultPromptInputRef.current.input;

  return (
    <ChatPanel
      apiUrl={import.meta.env.VITE_REACT_APP_API_PATH}
      chatSessionsFilter={chatSessionsFilter}
      className="bg-card w-full"
      defaultPromptInput={defaultPromptInput}
      platform={MCPPlatform.INBOX}
      promptInput={promptInput}
      resourceLinkHandlers={resourceLinkHandlers}
      suggestionInsert={{ onInsert }}
      toolFilter={AUTHORING_CHAT_TOOL_FILTER}
      toolFilterStorageKey={`authoring-playbook-${playbookId}`}
      supportsMultipleSessions
    />
  );
};
